import { useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { Dialog } from "@mui/material";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  isCompanySettingsReadOnly,
  VStack,
} from "../../components/utils";
import {
  useGetCustomFields,
  useCreateCustomField,
  useUpdateCustomField,
  useDeactivateCustomField,
} from "../../queries/customFields";
import {
  CustomField,
  UdfEntityType,
  UdfDataType,
  CreateCustomFieldDto,
  UpdateCustomFieldDto,
} from "../../types/CustomFields";
import AlertDialog from "../../components/shared/AlertDialog";

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

const RESERVED_EMPLOYEE = new Set([
  "address",
  "manager",
  "contactnumber",
  "pan",
  "department",
  "designation",
  "residentialstatus",
  "nationality",
  "workingcountry",
  "legalname",
  "name",
  "employmentstatus",
  "employmenttype",
  "email",
  "employeeidentification",
  "dateofjoining",
  "resignationdate",
  "lastday",
  "companyid",
  "salary",
  "grade",
  "subsidiary",
  "demataccountid",
  "personalemail",
  "tags",
  "nominee1",
  "nominee2",
  "relationship1",
  "relationship2",
  "percentage1",
  "percentage2",
  "contact1",
  "contact2",
  "email1",
  "email2",
  "dob1",
  "dob2",
  "address1",
  "address2",
  "employeeid",
  "employeename",
  "statusofemployment",
  "emailaddress",
  "countryresiding",
  "registeredaddress",
  "employername",
  "dateofleaving",
]);

const RESERVED_GRANT = new Set([
  "employeename",
  "employeeid",
  "optionsgranted",
  "planname",
  "dateofgrant",
  "grantprice",
  "vestingtemplate",
  "vestingdatetype",
  "vestingdate",
  "actualvestingday",
  "granttype",
  "tags",
  "grantlettertemplate",
  "grantidentifier",
  "allocationmatrix",
  "userecommended",
  "valueofoptionsgranted",
  "note",
  "remarks",
]);

function isReserved(
  name: string,
  fieldPath: string,
  entityType: UdfEntityType
): boolean {
  const reserved =
    entityType === UdfEntityType.EMPLOYEE ? RESERVED_EMPLOYEE : RESERVED_GRANT;
  return reserved.has(normalize(name)) || reserved.has(normalize(fieldPath));
}

interface FieldFormProps {
  entityType: UdfEntityType;
  field?: CustomField;
  onClose: () => void;
}

const DATA_TYPE_OPTIONS: { value: UdfDataType; label: string }[] = [
  { value: UdfDataType.STRING, label: "Text" },
  { value: UdfDataType.NUMBER, label: "Number" },
  { value: UdfDataType.DATE, label: "Date" },
  { value: UdfDataType.DROPDOWN, label: "Dropdown" },
];

const EMPTY_FORM: CreateCustomFieldDto = {
  name: "",
  fieldPath: "",
  entityType: UdfEntityType.EMPLOYEE,
  dataType: UdfDataType.STRING,
  placeholder: "",
  description: "",
  isRequired: false,
  validations: {},
  valueSet: [],
};

function FieldForm({ entityType, field, onClose }: FieldFormProps) {
  const isEdit = Boolean(field);
  const createMutation = useCreateCustomField();
  const updateMutation = useUpdateCustomField();

  const [form, setForm] = useState<CreateCustomFieldDto>({
    ...EMPTY_FORM,
    entityType,
    ...(field
      ? {
          name: field.name,
          fieldPath: field.fieldPath,
          dataType: field.dataType,
          placeholder: field.placeholder ?? "",
          description: field.description ?? "",
          isRequired: field.isRequired,
          validations: field.validations ?? {},
          valueSet: field.valueSet ?? [],
        }
      : {}),
  });

  const [newOption, setNewOption] = useState("");
  const [nameError, setNameError] = useState("");

  const set = (key: keyof CreateCustomFieldDto, value: any) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleNameChange = (value: string) => {
    set("name", value);
    if (!isEdit) {
      set(
        "fieldPath",
        value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_|_$/g, "")
      );
    }
    const fp = value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    setNameError(
      isReserved(value, fp, entityType)
        ? `"${value}" conflicts with a standard field. Choose a different name.`
        : ""
    );
  };

  const handleFieldPathChange = (value: string) => {
    const fp = value.toLowerCase().replace(/[^a-z0-9_]/g, "_");
    set("fieldPath", fp);
    setNameError(
      isReserved(form.name, fp, entityType)
        ? `"${form.name}" conflicts with a standard field. Choose a different name.`
        : ""
    );
  };

  const setValidation = (key: string, value: any) =>
    setForm((f) => ({
      ...f,
      validations: { ...f.validations, [key]: value || undefined },
    }));

  const addOption = () => {
    const opt = newOption.trim();
    if (!opt) return;
    if ((form.valueSet ?? []).includes(opt)) {
      toast.warn("Option already exists");
      return;
    }
    set("valueSet", [...(form.valueSet ?? []), opt]);
    setNewOption("");
  };

  const removeOption = (opt: string) =>
    set(
      "valueSet",
      (form.valueSet ?? []).filter((o) => o !== opt)
    );

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Field label is required");
      return;
    }
    if (nameError) {
      toast.error(nameError);
      return;
    }
    if (
      form.dataType === UdfDataType.DROPDOWN &&
      !(form.valueSet ?? []).length
    ) {
      toast.error("Dropdown must have at least one option");
      return;
    }
    try {
      if (isEdit && field) {
        const dto: UpdateCustomFieldDto = {
          name: form.name,
          dataType: form.dataType,
          placeholder: form.placeholder,
          description: form.description,
          isRequired: form.isRequired,
          validations: form.validations,
          valueSet: form.valueSet,
        };
        await updateMutation.mutateAsync({ id: field.id, dto });
        toast.success("Custom field updated");
      } else {
        await createMutation.mutateAsync(form);
        toast.success("Custom field created");
      }
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to save field");
    }
  };

  const inputClass =
    "border border-gray-300 rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-1 focus:ring-orange-400";
  const labelClass = "text-xs font-medium text-gray-600 mb-1";

  return (
    <div className="p-6 w-[520px]">
      <HStack className="items-center justify-between mb-4">
        <h3 className="text-base font-semibold">
          {isEdit ? "Edit" : "Add"} Custom Field —{" "}
          {entityType === UdfEntityType.EMPLOYEE ? "Employee" : "Grant"}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <Icon icon="mdi:close" className="w-5 h-5" />
        </button>
      </HStack>

      <VStack className="gap-4">
        <div>
          <label className={labelClass}>Label *</label>
          <input
            className={`${inputClass} ${
              nameError ? "border-red-400 focus:ring-red-400" : ""
            }`}
            placeholder="e.g. Sub Department"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
          />
          {nameError && (
            <p className="mt-1 text-xs text-red-500">{nameError}</p>
          )}
        </div>

        {!isEdit && (
          <div>
            <label className={labelClass}>
              Field Key{" "}
              <span className="text-gray-400 font-normal">
                (auto-filled, must be unique)
              </span>
            </label>
            <input
              className={inputClass}
              placeholder="e.g. sub_department"
              value={form.fieldPath}
              onChange={(e) => handleFieldPathChange(e.target.value)}
            />
          </div>
        )}

        {/* Data type */}
        <div>
          <label className={labelClass}>Data Type *</label>
          <select
            className={inputClass}
            value={form.dataType}
            onChange={(e) => set("dataType", e.target.value as UdfDataType)}
          >
            {DATA_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Dropdown options */}
        {form.dataType === UdfDataType.DROPDOWN && (
          <div>
            <label className={labelClass}>Options</label>
            <HStack className="gap-2 mb-2">
              <input
                className={`${inputClass} flex-1`}
                placeholder="Add option…"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addOption()}
              />
              <ButtonPrimary1
                onClick={addOption}
                className="text-xs px-3 py-1.5"
              >
                Add
              </ButtonPrimary1>
            </HStack>
            <div className="flex flex-wrap gap-2">
              {(form.valueSet ?? []).map((opt) => (
                <span
                  key={opt}
                  className="flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                >
                  {opt}
                  <button
                    onClick={() => removeOption(opt)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <Icon icon="mdi:close" className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Placeholder */}
        <div>
          <label className={labelClass}>Placeholder</label>
          <input
            className={inputClass}
            placeholder="Hint shown inside the input"
            value={form.placeholder}
            onChange={(e) => set("placeholder", e.target.value)}
          />
        </div>

        {/* Description */}
        <div>
          <label className={labelClass}>Description / Help text</label>
          <input
            className={inputClass}
            placeholder="Brief explanation of this field"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>

        {/* Validations */}
        {form.dataType === UdfDataType.STRING && (
          <HStack className="gap-4">
            <div className="flex-1">
              <label className={labelClass}>Min length</label>
              <input
                type="number"
                className={inputClass}
                placeholder="0"
                value={form.validations?.min ?? ""}
                onChange={(e) =>
                  setValidation(
                    "min",
                    e.target.value ? parseInt(e.target.value, 10) : undefined
                  )
                }
              />
            </div>
            <div className="flex-1">
              <label className={labelClass}>Max length</label>
              <input
                type="number"
                className={inputClass}
                placeholder="—"
                value={form.validations?.max ?? ""}
                onChange={(e) =>
                  setValidation(
                    "max",
                    e.target.value ? parseInt(e.target.value, 10) : undefined
                  )
                }
              />
            </div>
          </HStack>
        )}

        {form.dataType === UdfDataType.NUMBER && (
          <HStack className="gap-4">
            <div className="flex-1">
              <label className={labelClass}>Min value</label>
              <input
                type="number"
                className={inputClass}
                placeholder="—"
                value={form.validations?.min ?? ""}
                onChange={(e) =>
                  setValidation(
                    "min",
                    e.target.value ? parseFloat(e.target.value) : undefined
                  )
                }
              />
            </div>
            <div className="flex-1">
              <label className={labelClass}>Max value</label>
              <input
                type="number"
                className={inputClass}
                placeholder="—"
                value={form.validations?.max ?? ""}
                onChange={(e) =>
                  setValidation(
                    "max",
                    e.target.value ? parseFloat(e.target.value) : undefined
                  )
                }
              />
            </div>
          </HStack>
        )}

        {form.dataType === UdfDataType.DATE && (
          <HStack className="gap-4">
            <div className="flex-1">
              <label className={labelClass}>Earliest date</label>
              <input
                type="date"
                className={inputClass}
                value={form.validations?.dateMin ?? ""}
                onChange={(e) => setValidation("dateMin", e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className={labelClass}>Latest date</label>
              <input
                type="date"
                className={inputClass}
                value={form.validations?.dateMax ?? ""}
                onChange={(e) => setValidation("dateMax", e.target.value)}
              />
            </div>
          </HStack>
        )}

        {/* Required toggle */}
        <HStack className="items-center gap-3">
          <input
            id="isRequired"
            type="checkbox"
            className="w-4 h-4 accent-orange-500"
            checked={form.isRequired}
            onChange={(e) => set("isRequired", e.target.checked)}
          />
          <label htmlFor="isRequired" className="text-sm text-gray-700">
            This field is required
          </label>
        </HStack>

        {/* Actions */}
        <HStack className="justify-between gap-3 pt-2">
          <ButtonPrimary1 onClick={onClose}>Cancel</ButtonPrimary1>
          <ButtonPrimary
            onClick={handleSubmit}
            loading={createMutation.isLoading || updateMutation.isLoading}
          >
            {isEdit ? "Save Changes" : "Add Field"}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </div>
  );
}

function FieldRow({
  field,
  onEdit,
  onDeactivate,
  disabled,
}: {
  field: CustomField;
  onEdit: () => void;
  onDeactivate: () => void;
  disabled: boolean;
}) {
  const DATA_TYPE_LABEL: Record<UdfDataType, string> = {
    [UdfDataType.STRING]: "Text",
    [UdfDataType.NUMBER]: "Number",
    [UdfDataType.DATE]: "Date",
    [UdfDataType.DROPDOWN]: "Dropdown",
  };

  return (
    <HStack className="items-center justify-between px-4 py-3 border-b last:border-0 hover:bg-gray-50">
      <VStack className="gap-0.5 flex-1">
        <HStack className="items-center gap-2">
          <span className="text-sm font-medium text-gray-800">
            {field.name}
          </span>
          {field.isRequired && (
            <span className="text-xs bg-red-50 text-red-500 px-1.5 py-0.5 rounded">
              Required
            </span>
          )}
          <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">
            {DATA_TYPE_LABEL[field.dataType as UdfDataType]}
          </span>
        </HStack>
        <span className="text-xs text-gray-400 font-mono">
          {field.fieldPath}
        </span>
        {field.description && (
          <span className="text-xs text-gray-500 mt-0.5">
            {field.description}
          </span>
        )}
      </VStack>

      <HStack className="gap-2">
        <button
          onClick={onEdit}
          disabled={disabled}
          className="text-xs text-orange-500 hover:underline font-medium"
        >
          Edit
        </button>
        <button
          onClick={onDeactivate}
          disabled={disabled}
          className="text-xs text-red-400 hover:underline font-medium"
        >
          Deactivate
        </button>
      </HStack>
    </HStack>
  );
}

function FieldSection({
  title,
  entityType,
}: {
  title: string;
  entityType: UdfEntityType;
}) {
  const isUserReadOnly = isCompanySettingsReadOnly();
  const [expanded, setExpanded] = useState(false);
  const [dialog, setDialog] = useState<{
    open: boolean;
    field?: CustomField;
  }>({ open: false });
  const [deactivateDialog, setDeactivateDialog] = useState<{
    open: boolean;
    field?: CustomField;
  }>({ open: false });

  const { data: fields = [] } = useGetCustomFields(entityType);
  const deactivateMutation = useDeactivateCustomField();

  const handleDeactivate = async () => {
    if (!deactivateDialog.field) return;
    try {
      await deactivateMutation.mutateAsync(deactivateDialog.field.id);
      toast.success("Custom field deactivated. Existing data is preserved.");
    } catch {
      toast.error("Failed to deactivate field");
    }
    setDeactivateDialog({ open: false });
  };

  return (
    <>
      {/* Accordion header */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 border-b text-left"
        onClick={() => setExpanded((e) => !e)}
      >
        <HStack className="items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
            {fields.length} field{fields.length !== 1 ? "s" : ""}
          </span>
        </HStack>
        <Icon
          icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"}
          className="w-5 h-5 text-gray-500"
        />
      </button>

      {/* Accordion body */}
      {expanded && (
        <Box className="border-b">
          {fields.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-400">
              No custom fields yet. Add one below.
            </p>
          ) : (
            fields.map((f) => (
              <FieldRow
                key={f.id}
                field={f}
                disabled={isUserReadOnly}
                onEdit={() => setDialog({ open: true, field: f })}
                onDeactivate={() =>
                  setDeactivateDialog({ open: true, field: f })
                }
              />
            ))
          )}

          <Box className="px-4 py-3">
            <ButtonPrimary
              className="text-xs"
              onClick={() => setDialog({ open: true, field: undefined })}
              disabled={isUserReadOnly}
            >
              <Icon icon="mdi:plus" className="w-4 h-4 mr-1" />
              Add {title} Field
            </ButtonPrimary>
          </Box>
        </Box>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false })}>
        <FieldForm
          entityType={entityType}
          field={dialog.field}
          onClose={() => setDialog({ open: false })}
        />
      </Dialog>

      {/* Deactivate confirmation */}
      <AlertDialog
        open={deactivateDialog.open}
        message={`Deactivate "${deactivateDialog.field?.name}"? Existing values are preserved but the field will no longer appear in forms.`}
        primaryActionText="Deactivate"
        onPrimaryAction={handleDeactivate}
        onClose={() => setDeactivateDialog({ open: false })}
      />
    </>
  );
}

function CustomFields() {
  return (
    <div className="w-full bg-white rounded-md">
      <Box className="px-6 py-4 border-b">
        <h3 className="text-base font-semibold text-gray-800">Custom Fields</h3>
        <p className="text-sm text-gray-500 mt-1">
          Define your own fields for employees and grants. Custom fields appear
          in forms, Excel templates, exports, and grant letter merge tags.
        </p>
      </Box>

      <VStack className="divide-y">
        <FieldSection
          title="Employee Fields"
          entityType={UdfEntityType.EMPLOYEE}
        />
        <FieldSection title="Grant Fields" entityType={UdfEntityType.GRANT} />
      </VStack>
    </div>
  );
}

export default CustomFields;
